import { useEffect, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover.jsx';
import { Input } from '@/components/ui/input.jsx';
import { Button } from '@/components/ui/button.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { setDealFees } from '@/services/deals.service.js';

// The deal's fees, beside the lock key in the deal header (David 2026-10-27):
//   platform fee  standard, reduced or WAIVED, with a reason that stays on the
//                 ledger and is never shown to the client
//   agency fee    blank is none (no line on the client's bill); never on the
//                 application, which goes to the carrier
// The state and the client's wording are the server's (deal.fees,
// services/deal-fees.service feeStateForDeal); this derives nothing. A paid bind
// or an open payment request refuses the change, and the server's sentence says which.
const money = (n) => `$${Number(n).toFixed(2)}`;
const digits = (v) => v.replace(/[^0-9.]/g, '');

export default function DealFeesKey({ dealId, fees, onChange }) {
  const [open, setOpen] = useState(false);
  const [platform, setPlatform] = useState('');
  const [reason, setReason] = useState('');
  const [agency, setAgency] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !fees || fees.ok !== true) return;
    setPlatform(fees.state === 'standard' ? '' : String(fees.amount));
    setReason(fees.reason || '');
    setAgency(fees.agency_fee != null ? String(fees.agency_fee) : '');
  }, [open, fees]);

  const base = 'inline-flex items-center gap-1 rounded px-1 py-px text-xs';
  if (!fees || fees.ok !== true) return <span className={`${base} text-text-dim`} title="The fees could not be read">fees: could not check</span>;
  const platformWord = fees.state === 'waived' ? 'fee waived' : fees.state === 'reduced' ? `fee ${money(fees.amount)} (reduced)` : `fee ${money(fees.amount)}`;
  const word = fees.agency_fee != null ? `${platformWord} + agency ${money(fees.agency_fee)}` : platformWord;
  const locked = fees.leg && fees.leg.settled;
  const changed = fees.state !== 'standard' || fees.agency_fee != null;

  const typedPlatform = platform.trim() === '' ? null : Number(platform);
  const platformOk = typedPlatform === null || (Number.isFinite(typedPlatform) && typedPlatform >= 0 && typedPlatform <= fees.standard);
  const typedAgency = agency.trim() === '' ? null : Number(agency);
  const agencyOk = typedAgency === null || (Number.isFinite(typedAgency) && typedAgency >= 0);
  const needsReason = typedPlatform !== null && typedPlatform < fees.standard;

  async function save(next) {
    setBusy(true);
    try {
      const res = await setDealFees(dealId, next);
      if (!res?.success) throw new Error(res?.error || 'failed');
      const now = res.fees;
      const said = [
        now.state === 'waived' ? 'platform fee waived' : now.state === 'reduced' ? `platform fee ${money(now.amount)} (reduced)` : `platform fee ${money(now.amount)}`,
        now.agency_fee != null ? `agency fee ${money(now.agency_fee)}` : 'no agency fee',
      ].join(', ');
      toast.success(`Fees saved: ${said}`);
      setOpen(false);
      onChange?.();
    } catch (err) {
      toast.error(`Fees not saved: ${err?.body?.error || err?.message || 'failed'}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={locked}
          title={locked ? 'This bind is paid, so its fees are history' : 'Set this deal\'s fees'}
          data-deal-fees={fees.state}
          className={`${base} hover:bg-surface-strong disabled:opacity-60 ${changed ? 'font-medium text-text' : 'text-text-muted hover:text-text'}`}
        >
          {word}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" side="bottom" sideOffset={4} className="w-80 p-3">
        <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-text-dim">Fees</div>
        <label className="mb-2 block text-xs text-text-muted">
          Platform fee (standard {money(fees.standard)}; 0 waives it)
          <Input inputMode="decimal" value={platform} placeholder={String(fees.standard)} onChange={(e) => setPlatform(digits(e.target.value))} className="mt-1" />
        </label>
        {needsReason ? (
          <label className="mb-2 block text-xs text-text-muted">
            Reason (yours, never shown to the client)
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. second policy, one checkout" className="mt-1" />
          </label>
        ) : null}
        <label className="mb-3 block text-xs text-text-muted">
          Agency fee (blank for none; on the bill and statement, never the application)
          <Input inputMode="decimal" value={agency} placeholder="none" onChange={(e) => setAgency(digits(e.target.value))} className="mt-1" />
        </label>
        {!platformOk ? <p className="mb-2 text-xs text-danger">Platform fee: from $0 up to {money(fees.standard)}.</p> : null}
        {!agencyOk ? <p className="mb-2 text-xs text-danger">Agency fee: a dollar amount, or blank for none.</p> : null}
        <div className="flex items-center justify-between gap-2">
          {changed ? (
            <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={() => save({ platformFee: null, reason: null, agencyFee: null })}>Back to standard</Button>
          ) : <span />}
          <Button
            type="button"
            size="sm"
            disabled={busy || !platformOk || !agencyOk || (needsReason && !reason.trim())}
            onClick={() => save({ platformFee: typedPlatform, reason: needsReason ? reason : null, agencyFee: typedAgency })}
          >
            {busy ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
